// Top-of-report KPI strip — 5 large-font metrics in a row. Mirrors TradingView's
// Strategy Report header (Total P&L / Max equity DD / Total trades / Profitable trades %
// / Profit factor).

import type { BacktestResponseDto } from "../../api/types";
import { selectPrimary } from "./primary";

interface Props {
  result: BacktestResponseDto;
}

export function KpiStrip({ result }: Props) {
  // Phase 12: prefer the netting account (result.strategy); fall back to legacy merged,
  // then the single-direction scans. TV shows one row — this is "the strategy".
  const primary = selectPrimary(result);
  if (!primary) {
    return (
      <div style={{ padding: 12, background: "#161b22", borderRadius: 6, fontSize: 12, color: "#8b949e" }}>
        No backtest data.
      </div>
    );
  }

  const totalReturn = primary.totalReturn;
  const maxDD = "maxDD" in primary ? primary.maxDD : primary.maxStratDD;
  const trades = "trades" in primary
    ? (primary as { trades: { length: number }[] | number }).trades
    : 0;
  const tradesCount = typeof trades === "number"
    ? trades
    : (result.merged ? result.merged.longTrades + result.merged.shortTrades : 0);
  const winRate = primary.winRate;
  const profitFactor = primary.profitFactor;

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(5, 1fr)",
        gap: 12,
        padding: 16,
        background: "#0d1117",
        border: "1px solid #30363d",
        borderRadius: 6,
      }}
    >
      <Cell label="Total P&L" value={fmtPct(totalReturn)} accent={signColor(totalReturn)} />
      <Cell label="Max equity drawdown" value={fmtPct(maxDD)} accent={maxDD < 0 ? "#f85149" : "#c9d1d9"} />
      <Cell label="Total trades" value={tradesCount.toLocaleString()} />
      <Cell label="Profitable trades" value={`${fmt(winRate)}%`} accent={winRate >= 50 ? "#3fb950" : "#c9d1d9"} />
      <Cell label="Profit factor" value={fmt(profitFactor)} accent={profitFactor >= 1 ? "#3fb950" : "#f85149"} />
    </div>
  );
}

function Cell({ label, value, accent }: { label: string; value: string; accent?: string }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: "#8b949e", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 600, color: accent ?? "#c9d1d9", fontFamily: "system-ui, sans-serif" }}>
        {value}
      </div>
    </div>
  );
}

function fmt(n: number): string {
  if (!Number.isFinite(n)) return "—";
  return n.toFixed(2);
}

function fmtPct(n: number): string {
  if (!Number.isFinite(n)) return "—";
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(2)}%`;
}

function signColor(n: number): string {
  if (n > 0) return "#3fb950";
  if (n < 0) return "#f85149";
  return "#c9d1d9";
}
